import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged, ValueOf } from 'type-fest';
import { ImmutableEntity } from '../../common';
import type { CommandContext } from '../../common/context';
import { UniqueShortId } from '../../common/id';
import { ImportTableDefinition } from '../importTableDefinition';
import type { CompatibilityCheckInput as _CompatibilityCheckInput, CompatibilityResult as _CompatibilityResult } from './compatibility';
import { checkCompatibility as _checkCompatibility, toCompatibilityCheckInput as _toCompatibilityCheckInput } from './compatibility';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(21);

/**
 * 互換性種別
 *
 * - `initial`: 既存定義からの backfill で作られた初回 revision。
 * - `compatible`: 直前 revision に対し後方互換性が確実な変更。
 * - `possiblyBreaking`: 後方互換性がない可能性のある変更（今回未実装）。
 */
const _後方互換性Type = {
  initial: 'initial',
  compatible: 'compatible',
  possiblyBreaking: 'possiblyBreaking',
} as const;
type _後方互換性Type = ValueOf<typeof _後方互換性Type>;

/**
 * インポートテーブル定義リビジョン
 *
 * `ImportTableDefinition` のうち、取り込み解釈に影響する設定値を immutable に保持する。
 * Revision は作成後に内容を変更しない。`ImportTableDefinition.activeRevisionId` で公開済み
 * revision を、`lastRevisionId` で最新作成済み revision を表す。
 */
export type ImportTableDefinitionRevision = ImmutableEntity & {
  projectId: Project.Id;
  id: ImportTableDefinitionRevision.Id;
  importTableDefinitionId: ImportTableDefinition.Id;

  /** 1 から始まる連番。`(projectId, importTableDefinitionId, revisionNumber)` で一意。 */
  revisionNumber: number;

  schemaJson: ImportTableDefinition.SchemaJson;
  uniqueKeys: ImportTableDefinition.UniqueKeys;
  loadMode: ImportTableDefinition.LoadMode;
  インポート経路: ImportTableDefinition.インポート経路;

  後方互換性Type: _後方互換性Type;
  /** 直前 revision との差分サマリ。`initial` の場合は undefined。 */
  changeSummary: ImportTableDefinitionRevision.ChangeSummary | undefined;
};

export namespace ImportTableDefinitionRevision {
  export type Id = Tagged<UniqueShortId, 'importTableDefinitionRevisionId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export const checkCompatibility = _checkCompatibility;
  export const toCompatibilityCheckInput = _toCompatibilityCheckInput;
  export type CompatibilityCheckInput = _CompatibilityCheckInput;
  export type CompatibilityResult = _CompatibilityResult;

  export type CompatibilityType = _後方互換性Type;
  export namespace CompatibilityType {
    export const FromString = (s: string): CompatibilityType => {
      const value = _後方互換性Type[s as keyof typeof _後方互換性Type];
      if (!value) throw new Error(`Invalid ImportTableDefinitionRevision.CompatibilityType: ${s}`);
      return value;
    };
  }

  /**
   * 差分サマリ
   *
   * 互換性判定で検出した列単位の変更概要。スキーマは将来拡張するため、ここでは緩く定義する。
   */
  export type ChangeSummary = {
    addedColumns?: string[];
    removedColumns?: string[];
    changedColumns?: {
      columnName: string;
      reasons: string[];
    }[];
    /** 列の追加・削除・属性変更以外の差分（loadMode・uniqueKeys 等）。英語の技術メッセージが格納される。 */
    otherChanges?: string[];
  };

  export type Props = {
    id: string;
    projectId: string;
    importTableDefinitionId: string;
    revisionNumber: number;
    schemaJson: ImportTableDefinition.SchemaJson.Props;
    uniqueKeys: ImportTableDefinition.UniqueKeys.Props;
    loadMode: string;
    importMethod: string;
    compatibilityType: string;
    changeSummary: ChangeSummary | undefined;
  } & ImmutableEntity.Props;

  export const Construct = (props: Props): ImportTableDefinitionRevision => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      importTableDefinitionId: ImportTableDefinition.Id.FromString(props.importTableDefinitionId),
      revisionNumber: props.revisionNumber,
      schemaJson: ImportTableDefinition.SchemaJson.Construct(props.schemaJson),
      uniqueKeys: ImportTableDefinition.UniqueKeys.Construct(props.uniqueKeys),
      loadMode: ImportTableDefinition.LoadMode.FromString(props.loadMode),
      インポート経路: ImportTableDefinition.インポート経路.FromString(props.importMethod),
      後方互換性Type: CompatibilityType.FromString(props.compatibilityType),
      changeSummary: props.changeSummary,
      ...ImmutableEntity.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Pick<
      ImportTableDefinitionRevision.Props,
      | 'importTableDefinitionId'
      | 'revisionNumber'
      | 'schemaJson'
      | 'uniqueKeys'
      | 'loadMode'
      | 'importMethod'
      | 'compatibilityType'
      | 'changeSummary'
    >;
    export type Params = {
      context: CommandContext;
      props: Props;
    };
  }

  export const Create = (params: Create.Params): ImportTableDefinitionRevision => {
    const { context, props } = params;
    return Construct({
      id: Id.Generate(),
      projectId: context.projectId,
      importTableDefinitionId: props.importTableDefinitionId,
      revisionNumber: props.revisionNumber,
      schemaJson: props.schemaJson,
      uniqueKeys: props.uniqueKeys,
      loadMode: props.loadMode,
      importMethod: props.importMethod,
      compatibilityType: props.compatibilityType,
      changeSummary: props.changeSummary,
      ...ImmutableEntity.Create(context),
    });
  };
}
